import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizeType(value) {
  return String(value || "").trim().toLowerCase();
}

function getTypeMeta(typeValue) {
  const type = normalizeType(typeValue);
  if (type === "backup_health_alert") {
    return {
      label: "Backup",
      className: "bg-amber-100 text-amber-800",
    };
  }
  return {
    label: "Algemeen",
    className: "bg-slate-100 text-slate-700",
  };
}

export default function NotificationsPage() {
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState("all");

  const unreadItems = useMemo(() => items.filter((item) => !item?.readAt), [items]);
  const backupItems = useMemo(
    () => items.filter((item) => normalizeType(item?.type) === "backup_health_alert"),
    [items]
  );
  const filteredItems = useMemo(() => {
    if (activeFilter === "backup") return backupItems;
    if (activeFilter === "other") {
      return items.filter((item) => normalizeType(item?.type) !== "backup_health_alert");
    }
    return items;
  }, [activeFilter, backupItems, items]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/notifications/mine", { params: { limit: 100 } });
      setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
      setUnreadCount(Number(res?.data?.unreadCount || 0));
    } catch (err) {
      setError(err?.response?.data?.message || "Meldingen laden mislukt");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function markRead(id) {
    if (!id) return;
    try {
      setSavingId(String(id));
      await api.post(`/notifications/${id}/read`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Markeren als gelezen mislukt");
    } finally {
      setSavingId("");
    }
  }

  async function markAllUnreadAsRead() {
    if (unreadItems.length === 0) return;
    try {
      setSavingId("__all__");
      await Promise.all(unreadItems.map((item) => api.post(`/notifications/${item._id}/read`)));
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Alles gelezen markeren mislukt");
    } finally {
      setSavingId("");
    }
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Meldingen</h2>
            <p className="text-sm text-gray-500">
              Toewijzingen, updates en operationele signalen zoals backupmeldingen.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
              Ongelezen: {unreadCount}
            </span>
            <button
              type="button"
              onClick={markAllUnreadAsRead}
              disabled={savingId === "__all__" || unreadItems.length === 0}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {savingId === "__all__" ? "Bezig..." : "Alles gelezen"}
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveFilter("all")}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              activeFilter === "all"
                ? "bg-slate-800 text-white"
                : "border border-slate-300 bg-white text-slate-700"
            }`}
          >
            Alle ({items.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveFilter("backup")}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              activeFilter === "backup"
                ? "bg-amber-600 text-white"
                : "border border-amber-300 bg-amber-50 text-amber-800"
            }`}
          >
            Backup alerts ({backupItems.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveFilter("other")}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              activeFilter === "other"
                ? "bg-slate-700 text-white"
                : "border border-slate-300 bg-white text-slate-700"
            }`}
          >
            Overige ({Math.max(0, items.length - backupItems.length)})
          </button>
        </div>
      </section>

      {error ? (
        <section className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </section>
      ) : null}

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        {loading ? <p className="text-sm text-gray-500">Laden...</p> : null}

        {!loading && filteredItems.length === 0 ? (
          <p className="text-sm text-gray-500">Geen meldingen.</p>
        ) : null}

        {!loading && filteredItems.length > 0 ? (
          <div className="space-y-2">
            {filteredItems.map((item) => {
              const unread = !item?.readAt;
              const typeMeta = getTypeMeta(item?.type);
              return (
                <article
                  key={item._id}
                  className={`rounded-lg border p-3 ${
                    unread ? "border-blue-200 bg-blue-50/40" : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">{item?.title || "Melding"}</p>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${typeMeta.className}`}>
                          {typeMeta.label}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-700">{item?.message || "-"}</p>
                      <p className="mt-1 text-xs text-gray-500">{formatDateTime(item?.createdAt)}</p>
                    </div>
                    {unread ? (
                      <button
                        type="button"
                        onClick={() => markRead(item._id)}
                        disabled={savingId === String(item._id)}
                        className="rounded border border-blue-300 bg-white px-2 py-1 text-xs font-medium text-blue-700 disabled:opacity-60"
                      >
                        {savingId === String(item._id) ? "..." : "Gelezen"}
                      </button>
                    ) : (
                      <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-500">Gelezen</span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </section>
    </div>
  );
}
